/* eslint-disable react/no-unused-prop-types */

import React, { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableProps, Tooltip } from '@app/ui';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useColumns } from './getColumns';
import { Post } from '@app/social-media-posts/types';
import type { PostData } from './types';
import { getCarouselPostIds } from '@app/social-media-posts/carouselGroups';
import ResizableTable from '@app/components/common/Table';
import StickySocialPostsTableLayout from './StickySocialPostsTableLayout';

type TableRowSelection<T extends object = object> =
    TableProps<T>['rowSelection'];

export interface PostListProps {
    posts: any;
    postsOrder: string[];
    currency?: string;
    isSelectMode?: boolean;
    selected?: { [key: string]: boolean };
    setSelected?: React.Dispatch<
        React.SetStateAction<{ [key: string]: boolean }>
    >;
    onSelectPost?: (postId: number) => void;
    // selectionType?: string;
    // hasPremiumPostInsights?: boolean;
    onDetailedView?: (post: Post) => void;
    // collections?: { [key: string]: boolean };
    totals?: Record<string, any>;
    postLevelMetrics: boolean;
    organicAndPaidMetrics: boolean;
    showCollabPostInfo?: boolean;
}

const SocialPostsListV2: FC<PostListProps> = ({
    posts,
    postsOrder,
    selected,
    setSelected,
    isSelectMode,
    // currency,
    // totals,
    onDetailedView,
    onSelectPost,
    postLevelMetrics,
    organicAndPaidMetrics,
    showCollabPostInfo = false,
}) => {
    const { t } = useTranslation('measurement');

    const selectedRowKeys = useMemo(() => {
        return Object.entries(selected || {})
            .filter(([, value]) => value)
            .map(([key]) => key.toString());
    }, [selected]);

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        if (!setSelected) return;

        const newSelected: Record<string, boolean> = {};

        newSelectedRowKeys.forEach(id => {
            newSelected[id.toString()] = true; // Use string as key
        });

        setSelected(newSelected);
    };

    const rowSelection: TableRowSelection<PostData> = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const columns = useColumns({
        isSelectMode,
        postLevelMetrics,
        organicAndPaidMetrics,
        showCollabPostInfo,
        onDetailedView,
        onSelectPost,
    });

    const dataSource = useMemo(() => {
        return postsOrder.map(id => {
            const postIds = getCarouselPostIds(posts[id]);
            const post = {
                ...posts[id],
                isSelected: postIds.some(postId => !!selected[postId]),
            };

            const annotationCount =
                post?.sponsorAnnotationsDetails?.length || 0;

            // Apply multiplier logic
            if (!postLevelMetrics && !organicAndPaidMetrics) {
                post.impressions *= annotationCount;
                post.videoViews *= annotationCount;
                post.engagements *= annotationCount;
            }

            if (!postLevelMetrics && organicAndPaidMetrics) {
                post.organicImpressions *= annotationCount;
                post.organicVideoViews *= annotationCount;
                post.organicEngagements *= annotationCount;
                post.paidImpressions *= annotationCount;
                post.paidVideoViews *= annotationCount;
                post.paidEngagements *= annotationCount;
            }

            if (!post.authenticatedAndAuthorized) {
                post.organicImpressions = null;
                post.organicVideoViews = null;
                post.organicEngagements = null;
                post.paidImpressions = null;
                post.paidVideoViews = null;
                post.paidEngagements = null;
            }

            return post;
        });
    }, [postsOrder, posts, selected, postLevelMetrics, organicAndPaidMetrics]);

    return (
        <StickySocialPostsTableLayout
            data-test="posts-list"
            scrollContentToEndWhen={organicAndPaidMetrics}
            scrollContentToEndDependencies={[columns]}
        >
            <ResizableTable<PostData>
                className="posts-table-v2"
                pagination={false}
                rowKey={record => record.id.toString()}
                columns={columns}
                dataSource={dataSource}
                bordered
                size="small"
                scroll={{ x: 'max-content' }}
                sticky
                /*
                 * `sticky` enables the header sticky behaviour; its `top` is overridden in
                 * CSS using --app-header-bottom + --filters-sticky-offset, both written
                 * synchronously by the filters component on scroll. This avoids any React
                 * work per scroll event and keeps the header in lockstep with the filters.
                 */
                rowSelection={isSelectMode ? rowSelection : undefined}
                rowClassName={(_, index) =>
                    index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
                }
                summary={pageData => {
                    if (organicAndPaidMetrics) {
                        let orgImpressions = 0;
                        let orgVideoViews = 0;
                        let orgEngagements = 0;
                        let paidImpressions = 0;
                        let paidVideoViews = 0;
                        let paidEngagements = 0;

                        pageData.forEach(post => {
                            orgImpressions += post.organicImpressions || 0;
                            orgVideoViews += post.organicVideoViews || 0;
                            orgEngagements += post.organicEngagements || 0;
                            paidImpressions += post.paidImpressions || 0;
                            paidVideoViews += post.paidVideoViews || 0;
                            paidEngagements += post.paidEngagements || 0;
                        });

                        return (
                            <Table.Summary.Row>
                                <Table.Summary.Cell
                                    index={0}
                                    colSpan={6}
                                    className="list-table-v2-total"
                                >
                                    <b>
                                        {t('socialMediaExposure.table.total')}{' '}
                                        <Tooltip
                                            title={t(
                                                'socialMediaExposure.table.totalTooltip',
                                            )}
                                        >
                                            <QuestionCircleOutlined
                                                style={{
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        </Tooltip>
                                    </b>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6}>
                                    {orgImpressions.toLocaleString()}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7}>
                                    {orgVideoViews.toLocaleString()}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8}>
                                    {orgEngagements.toLocaleString()}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={9}>
                                    {paidImpressions.toLocaleString()}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={10}>
                                    {paidVideoViews.toLocaleString()}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={11}>
                                    {paidEngagements.toLocaleString()}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }

                    let totalImpressions = 0;
                    let totalVideoViews = 0;
                    let totalEngagements = 0;

                    pageData.forEach(
                        ({ impressions, videoViews, engagements }) => {
                            totalImpressions += impressions || 0;
                            totalVideoViews += videoViews || 0;
                            totalEngagements += engagements || 0;
                        },
                    );

                    return (
                        <Table.Summary.Row>
                            <Table.Summary.Cell
                                index={0}
                                colSpan={columns.length - 3}
                                className="list-table-v2-total"
                            >
                                <b>
                                    {t('socialMediaExposure.table.total')}{' '}
                                    <Tooltip
                                        title={t(
                                            'socialMediaExposure.table.totalTooltip',
                                        )}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Tooltip>
                                </b>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell
                                index={columns.length - 3}
                                align="left"
                            >
                                {totalImpressions.toLocaleString()}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell
                                index={columns.length - 2}
                                align="left"
                            >
                                {totalVideoViews.toLocaleString()}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell
                                className="list-table-v2-engagements"
                                index={columns.length - 1}
                                align="left"
                            >
                                {totalEngagements.toLocaleString()}
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    );
                }}
            />
        </StickySocialPostsTableLayout>
    );
};

export default SocialPostsListV2;
